import React, { useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./shape-submenu.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import ShapeMenuIcon from "frontend/canvas-designer-new/shape-libraries/shape-menu-icon";
import FallbackLibraries from "../fallback-data/libraries.json";
import FallbackThumbnails from "../fallback-data/thumbnails.json";
import FallbackMappings from "../fallback-data/mapping.json";
import { ShapeLibraries, ShapeMapping, ShapeThumbnails } from "frontend/canvas-designer-new/shape-libraries/types";
import { ShapeInfo } from "frontend/canvas-designer-new/shape-libraries/side-panel/virtualized-list/virtualized-row";
import * as icons from "./connector-icons";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow } from "@floating-ui/react";
import cn from "classnames";

const LIBRARY_ID = "basicShapes";
const GROUP_ID = "basicShapes";
const SHAPES_TO_SHOW = [
  "BasicRectangle",
  "BasicRoundedRectangle",
  "BasicCircle",
  "BasicTriangle",
  "BasicDiamond",
  "BasicHexagon",
  "BasicPentagon",
  "BasicStar",
  "BasicTrapezoid",
  "BasicCloud",
  "BasicCallout",
  "BasicRightArrow",
];

export type ConnectorTestIdType = "connector-curve" | "connector-plain" | "connector-line" | "connector-elbow";

const CONNECTORS = [
  {
    lineType: "curve",
    showArrows: true,
    icon: icons.curvedLineWithArrowIcon,
    testid: "connector-curve",
  },
  {
    lineType: "line",
    showArrows: false,
    icon: icons.plainLineIcon,
    testid: "connector-plain",
  },
  {
    lineType: "line",
    showArrows: true,
    icon: icons.straightLineWithArrowIcon,
    testid: "connector-line",
  },
  {
    lineType: "elbow",
    showArrows: true,
    icon: icons.elbowLineWithArrowIcon,
    testid: "connector-elbow",
  },
];

export default function ShapeSubmenu({
  onMenuItemClick,
  onConnectorClick,
  onExpand,
  relativeTo,
  onHover,
  onLeave,
}: {
  onMenuItemClick: (
    elementType: string,
    elementData: Record<string, unknown>,
    elementInfo: ShapeInfo,
    closeSubmenu?: boolean
  ) => void;
  onConnectorClick: (lineType: string, showArrows: boolean) => void;
  onExpand: () => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const arrowRef = useRef(null);

  // Set up floating UI
  const { refs, floatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs, relativeTo]);

  // In order to avoid load times, we are hardcoding the fallback data for now.
  // This change only affects the small submenu which has hardcided shapes anyway.
  const shapeLibraries: ShapeLibraries = FallbackLibraries;
  const data: { thumbnails: ShapeThumbnails; mappings: ShapeMapping } = {
    thumbnails: FallbackThumbnails,
    mappings: FallbackMappings,
  };

  const library = shapeLibraries[LIBRARY_ID];
  const portal = document.getElementById(PortalID.Floaters);

  // Render content function
  const renderContent = () => (
    <>
      <>
        <div className={styles.itemsContainer} style={{ padding: 0 }}>
          {CONNECTORS.map((connector) => (
            <ShapeMenuIcon
              key={`connector-${connector.testid}`}
              shapeId={`connector-${connector.lineType}`}
              thumbnail={connector.icon}
              onClick={() => {
                onConnectorClick(connector.lineType, connector.showArrows);
              }}
              size={26}
              testid={connector.testid}
            />
          ))}
        </div>
        <div className={styles.separator} />
      </>
      <div className={styles.itemsContainer}>
        {SHAPES_TO_SHOW.map((shapeId) => {
          const thumbnail = data.thumbnails[shapeId];
          const { elementType, elementData } = data.mappings[shapeId];
          const elementInfo: ShapeInfo = {
            library: LIBRARY_ID,
            group: GROUP_ID,
            item: shapeId,
          };

          const displayName =
            library.groups[0].items.find((item: { id: string }) => item.id === shapeId)?.displayName ?? "";

          return (
            <ShapeMenuIcon
              key={`shape-${shapeId}`}
              shapeId={shapeId}
              displayName={displayName}
              thumbnail={thumbnail}
              onClick={() => {
                onMenuItemClick(elementType, elementData, elementInfo);
              }}
              size={26}
            />
          );
        })}
      </div>
      {isCimReorderEnabled && <div className={styles.separator} />}
      <button
        className={cn(styles.allShapesButton, isCimReorderEnabled && styles.allShapesButtonCIM)}
        onClick={onExpand}
      >
        {isCimReorderEnabled ? "More" : "All"} Shapes
      </button>
    </>
  );

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={styles.container}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          {renderContent()}
        </div>
      </FloatingPortal>
    );
  }

  // Original logic for non-CIM footer
  const element = relativeTo.current ? (
    <>
      <Floater relativeTo={relativeTo} side={"right"}>
        <div className={styles.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          {renderContent()}
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        extraStyle={{
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={styles.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
